"use client";

import React, { useState, useEffect, useContext } from "react";
import dynamic from "next/dynamic";
// Import LazyLoadResource dynamically to avoid SSR issues with react-lazyload
const LazyLoadResource = dynamic(() => import("@stadium/ui/src/components/lazy-load"), {
  ssr: false,
});
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

// SSR-compatible LazyLoadResource wrapper
// Renders children directly on server, applies lazy loading only on client
const SSRLazyLoadResource = ({ children, placeholder, ...rest }) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // On server and initial client render, render children directly
  // After hydration, apply lazy loading
  if (!isMounted) {
    return <>{children}</>;
  }

  return (
    <LazyLoadResource {...rest} placeholder={placeholder}>
      {children}
    </LazyLoadResource>
  );
};

// Import all the dynamic components (same as in the original file)
const Banner = dynamic(() =>
  import(/* webpackChunkName: "CMSbanner" */ "@sendmagic/prismic-nextjs/src/components/Banner"),
);
const HomeBanner = dynamic(() =>
  import(
    /* webpackChunkName: "CMShomeBanner" */ "@sendmagic/prismic-nextjs/src/components/HomeBanner"
  ),
);
const Features = dynamic(() =>
  import(/* webpackChunkName: "CMSfeatures" */ "@sendmagic/prismic-nextjs/src/components/Features"),
);
const TextWithIconsOnRight = dynamic(() =>
  import(
    /* webpackChunkName: "CMStwIcOnRight" */ "@sendmagic/prismic-nextjs/src/components/TextWithIconsOnRight"
  ),
);
const TextWithRightImage = dynamic(() =>
  import(
    /* webpackChunkName: "CMStwRiImg" */ "@sendmagic/prismic-nextjs/src/components/TextWithRightImage"
  ),
);
const TextWithRightIcons = dynamic(() =>
  import(
    /* webpackChunkName: "CMStwRiIcons" */ "@sendmagic/prismic-nextjs/src/components/TextWithRightIcons"
  ),
);
const TextWithSmallImage = dynamic(() =>
  import(
    /* webpackChunkName: "CMStwSmImg" */ "@sendmagic/prismic-nextjs/src/components/TextWithSmallImage"
  ),
);
const TextWithLeftImage = dynamic(() =>
  import(
    /* webpackChunkName: "CMStwLeImg" */ "@sendmagic/prismic-nextjs/src/components/TextWithLeftImage"
  ),
);
const TextWithHorizontalBullets = dynamic(() =>
  import(
    /* webpackChunkName: "CMStwHrBull" */ "@sendmagic/prismic-nextjs/src/components/TextWithHorizontalBullets"
  ),
);
const ButtonsBlock = dynamic(() =>
  import(
    /* webpackChunkName: "CMSbtnBlock" */ "@sendmagic/prismic-nextjs/src/components/ButtonsBlock"
  ),
);
const ContentBlock = dynamic(() =>
  import(
    /* webpackChunkName: "CMScoBlock" */ "@sendmagic/prismic-nextjs/src/components/ContentBlock"
  ),
);
const Separator = dynamic(() =>
  import(
    /* webpackChunkName: "CMSsepartor" */ "@sendmagic/prismic-nextjs/src/components/Separator"
  ),
);
const Testimonial = dynamic(() =>
  import(
    /* webpackChunkName: "CMStestimonial" */ "@sendmagic/prismic-nextjs/src/components/Testimonial"
  ),
);
const Press = dynamic(() =>
  import(/* webpackChunkName: "CMSpress" */ "@sendmagic/prismic-nextjs/src/components/Press"),
);
const CaseStudy = dynamic(() =>
  import(
    /* webpackChunkName: "CMScaseStudy" */ "@sendmagic/prismic-nextjs/src/components/CaseStudy"
  ),
);
const HowItWorks = dynamic(() =>
  import(
    /* webpackChunkName: "CMShwItWorks" */ "@sendmagic/prismic-nextjs/src/components/HowItWorks"
  ),
);
const SnackGiverGuarantee = dynamic(() =>
  import(
    /* webpackChunkName: "CMSsgg" */ "@sendmagic/prismic-nextjs/src/components/SnackGiverGuarantee"
  ),
);
const ProductSlider = dynamic(() =>
  import(
    /* webpackChunkName: "CMSprSlider" */ "@sendmagic/prismic-nextjs/src/components/ProductSlider"
  ),
);
const SnackmagicSnackboxComparison = dynamic(() =>
  import(
    /* webpackChunkName: "CMSsmSnackComp" */ "@sendmagic/prismic-nextjs/src/components/SnackmagicSnackboxComparison"
  ),
);
const Timeline = dynamic(() =>
  import(/* webpackChunkName: "CMStimeline" */ "@sendmagic/prismic-nextjs/src/components/Timeline"),
);
const Faq = dynamic(() =>
  import(/* webpackChunkName: "CMSfaq" */ "@sendmagic/prismic-nextjs/src/components/Faq"),
);
const ThreeImageBanner = dynamic(() =>
  import(
    /* webpackChunkName: "CMS3ImgBanner" */ "@sendmagic/prismic-nextjs/src/components/ThreeImageBanner"
  ),
);
const CustomizationDetail = dynamic(() =>
  import(
    /* webpackChunkName: "CMScustomDetail" */ "@sendmagic/prismic-nextjs/src/components/CustomizationDetail"
  ),
);
const FeaturesBlock = dynamic(() =>
  import(
    /* webpackChunkName: "CMSfeatBlock" */ "@sendmagic/prismic-nextjs/src/components/FeaturesBlock"
  ),
);
const FourImageRow = dynamic(() =>
  import(
    /* webpackChunkName: "CMS4ImgRow" */ "@sendmagic/prismic-nextjs/src/components/FourImageRow"
  ),
);
const ThreeImageRow = dynamic(() =>
  import(
    /* webpackChunkName: "CMS3ImgRow" */ "@sendmagic/prismic-nextjs/src/components/ThreeImageRow"
  ),
);
const TwoImageRow = dynamic(() =>
  import(
    /* webpackChunkName: "CMS2ImgRow" */ "@sendmagic/prismic-nextjs/src/components/TwoImageRow"
  ),
);
const Questions = dynamic(() =>
  import(/* webpackChunkName: "CMSques" */ "@sendmagic/prismic-nextjs/src/components/Questions"),
);
const TextWithCTAandOverlappingImage = dynamic(() =>
  import(
    /* webpackChunkName: "CMStextWCTAoverlapImg" */ "@sendmagic/prismic-nextjs/src/components/TextWithCTAandOverlappingImage"
  ),
);
const TextWithHeading = dynamic(() =>
  import(
    /* webpackChunkName: "CMStxtWHead" */ "@sendmagic/prismic-nextjs/src/components/TextWithHeading"
  ),
);
const TextOnlyBanner = dynamic(() =>
  import(
    /* webpackChunkName: "CMStxtOnlyBanner" */ "@sendmagic/prismic-nextjs/src/components/TextOnlyBanner"
  ),
);
const YellowBackgroundText = dynamic(() =>
  import(
    /* webpackChunkName: "CMSylwBgTxt" */ "@sendmagic/prismic-nextjs/src/components/YellowBackgroundText"
  ),
);
const HeadingWithPoints = dynamic(() =>
  import(
    /* webpackChunkName: "CMSheadWPoints" */ "@sendmagic/prismic-nextjs/src/components/HeadingWithPoints"
  ),
);
const Calendly = dynamic(() =>
  import(/* webpackChunkName: "CMScal" */ "@sendmagic/prismic-nextjs/src/components/Calendly"),
);
const HeadingWithButton = dynamic(() =>
  import(
    /* webpackChunkName: "CMSheadWBtn" */ "@sendmagic/prismic-nextjs/src/components/HeadingWithButton"
  ),
);
const Difference = dynamic(() =>
  import(/* webpackChunkName: "CMSdiff" */ "@sendmagic/prismic-nextjs/src/components/Difference"),
);
const LargeImageHeader = dynamic(() =>
  import(
    /* webpackChunkName: "CMSlgImgHead" */ "@sendmagic/prismic-nextjs/src/components/LargeImageHeader"
  ),
);
const Team = dynamic(() =>
  import(/* webpackChunkName: "CMSteam" */ "@sendmagic/prismic-nextjs/src/components/Team"),
);
const DidYouKnow = dynamic(() =>
  import(
    /* webpackChunkName: "CMSdidUKnw" */ "@sendmagic/prismic-nextjs/src/components/DidYouKnow"
  ),
);
const TextWithBackground = dynamic(() =>
  import(
    /* webpackChunkName: "CMStxtWBg" */ "@sendmagic/prismic-nextjs/src/components/TextWithBackground"
  ),
);
const ColoredBackgroundWithIcons = dynamic(() =>
  import(
    /* webpackChunkName: "CMScoBgWIcons" */ "@sendmagic/prismic-nextjs/src/components/ColoredBackgroundWithIcons"
  ),
);
const PricingBlock = dynamic(() =>
  import(
    /* webpackChunkName: "CMSpricBlock" */ "@sendmagic/prismic-nextjs/src/components/PricingBlock"
  ),
);
const PricingPackage = dynamic(() =>
  import(
    /* webpackChunkName: "CMSpricPack" */ "@sendmagic/prismic-nextjs/src/components/PricingPackage"
  ),
);
const ImageSlider = dynamic(() =>
  import(
    /* webpackChunkName: "CMSimgSlider" */ "@sendmagic/prismic-nextjs/src/components/ImageSlider"
  ),
);
const ExtendedCardsBlock = dynamic(() =>
  import(
    /* webpackChunkName: "CMSxtCardBlock" */ "@sendmagic/prismic-nextjs/src/components/ExtendedCardsBlock"
  ),
);
const ImageSliderWithMultipleRows = dynamic(() =>
  import(
    /* webpackChunkName: "CMSimgSlidWMuRows" */ "@sendmagic/prismic-nextjs/src/components/ImageSliderWithMultipleRows"
  ),
);
const FeaturesSmallIcons = dynamic(() =>
  import(
    /* webpackChunkName: "CMSfeatureSmIcons" */ "@sendmagic/prismic-nextjs/src/components/FeaturesSmallIcons"
  ),
);
const CustomerTestimonialSlider = dynamic(() =>
  import(
    /* webpackChunkName: "CMStestimonialSlider" */ "@sendmagic/prismic-nextjs/src/components/CustomerTestimonialSlider"
  ),
);
const MultipleVideoSlice = dynamic(() =>
  import(
    /* webpackChunkName: "CMSmultiVidSlice" */ "@sendmagic/prismic-nextjs/src/components/MultipleVideoSlice"
  ),
);
const TwoCtaSlice = dynamic(() =>
  import(
    /* webpackChunkName: "CMS2CtaSlice" */ "@sendmagic/prismic-nextjs/src/components/TwoCtaSlice"
  ),
);
const ImageGallery = dynamic(() =>
  import(
    /* webpackChunkName: "CMSimgGallery" */ "@sendmagic/prismic-nextjs/src/components/ImageGallery"
  ),
);
const HorizontalScrollableGallery = dynamic(() =>
  import(
    /* webpackChunkName: "CMShorizontalScrGallery" */ "@sendmagic/prismic-nextjs/src/components/HorizontalScrollableGallery"
  ),
);
const InvertedHowItWorks = dynamic(() =>
  import(
    /* webpackChunkName: "CMSinvertedHwItWrks" */ "@sendmagic/prismic-nextjs/src/components/InvertedHowItWorks"
  ),
);
const HeroSlice = dynamic(() =>
  import(
    /* webpackChunkName: "CMSheroSlice" */ "@sendmagic/prismic-nextjs/src/components/HeroSlice"
  ),
);
const EmbededResourceSlice = dynamic(() =>
  import(
    /* webpackChunkName: "CMSembedRsrceSlice" */ "@sendmagic/prismic-nextjs/src/components/EmbededResourceSlice"
  ),
);
const CalendarMoments = dynamic(() =>
  import(
    /* webpackChunkName: "CMScalMoments" */ "@sendmagic/prismic-nextjs/src/components/CalendarMoments"
  ),
);
const LongBlockWithTextAndImages = dynamic(() =>
  import(
    /* webpackChunkName: "CMSlgBlockWTxtImgs" */ "@sendmagic/prismic-nextjs/src/components/LongBlockWithTextAndImages"
  ),
);
const ListingBlock = dynamic(() =>
  import(
    /* webpackChunkName: "CMSlistBlock" */ "@sendmagic/prismic-nextjs/src/components/ListingBlock"
  ),
);
const HtmlEmbed = dynamic(() =>
  import(
    /* webpackChunkName: "CMShtmlEmbed" */ "@sendmagic/prismic-nextjs/src/components/HtmlEmbed"
  ),
);
const VerticalFeatureTwoImage = dynamic(() =>
  import(
    /* webpackChunkName: "CMSvertFeat2Img" */ "@sendmagic/prismic-nextjs/src/components/VerticalFeatureTwoImage"
  ),
);
const WhitepaperLandingPageSlice = dynamic(() =>
  import(
    /* webpackChunkName: "CMSwhitepaperLandingSlice" */ "@sendmagic/prismic-nextjs/src/components/WhitepaperLandingPageSlice"
  ),
);
const CountryStashes = dynamic(() =>
  import(
    /* webpackChunkName: "CMScountStashes" */ "@sendmagic/prismic-nextjs/src/components/CountryStashes"
  ),
);
const DownloadPdf = dynamic(() =>
  import(
    /* webpackChunkName: "CMSdownPDF" */ "@sendmagic/prismic-nextjs/src/components/DownloadPdf"
  ),
);
const NavigatorTab = dynamic(() =>
  import(
    /* webpackChunkName: "CMSnavTab" */ "@sendmagic/prismic-nextjs/src/components/NavigatorTab"
  ),
);
const FeaturesList = dynamic(() =>
  import(
    /* webpackChunkName: "CMSfeaturesList" */ "@sendmagic/prismic-nextjs/src/components/FeaturesList"
  ),
);
const ImagesStackContainer = dynamic(() =>
  import(
    /* webpackChunkName: "CMSimgStckContainer" */ "@sendmagic/prismic-nextjs/src/components/ImagesStackContainer"
  ),
);
const BackgroundImageWithText = dynamic(() =>
  import(
    /* webpackChunkName: "CMSbgImgWTxt" */ "@sendmagic/prismic-nextjs/src/components/BackgroundImageWithText"
  ),
);
const FourLargeImageRow = dynamic(() =>
  import(
    /* webpackChunkName: "CMS4LgImgRow" */ "@sendmagic/prismic-nextjs/src/components/FourLargeImageRow"
  ),
);
const CurvedBlockTextWithImage = dynamic(() =>
  import(
    /* webpackChunkName: "CMScrvdBlockTxtWImg" */ "@sendmagic/prismic-nextjs/src/components/CurvedBlockTextWithImage"
  ),
);
const TwoColumnsLayout = dynamic(() =>
  import(
    /* webpackChunkName: "CMS2ColLayout" */ "@sendmagic/prismic-nextjs/src/components/TwoColumnsLayout"
  ),
);
const HoverOverlays = dynamic(() =>
  import(
    /* webpackChunkName: "CMShoverOverlays" */ "@sendmagic/prismic-nextjs/src/components/HoverOverlays"
  ),
);
const CardSectionWithResponsiveBGImage = dynamic(() =>
  import(
    /* webpackChunkName: "CMScardSecWRespoBGImg" */ "@sendmagic/prismic-nextjs/src/components/CardSectionWithResponsiveBGImage"
  ),
);
const TextWithImagesAndCtas = dynamic(() =>
  import(
    /* webpackChunkName: "CMStxtWImgACtas" */ "@sendmagic/prismic-nextjs/src/components/TextWithImagesAndCtas"
  ),
);
const TextSliceSleek = dynamic(() =>
  import(
    /* webpackChunkName: "CMStxtSliceSleek" */ "@sendmagic/prismic-nextjs/src/components/TextSliceSleek"
  ),
);
const TableListings = dynamic(() =>
  import(
    /* webpackChunkName: "CMStableListings" */ "@sendmagic/prismic-nextjs/src/components/TableListings"
  ),
);
const TextWithThumbnail = dynamic(() =>
  import(
    /* webpackChunkName: "CMStxtWThumbnail" */ "@sendmagic/prismic-nextjs/src/components/TextWithThumbnail"
  ),
);
const InputAddress = dynamic(() =>
  import(
    /* webpackChunkName: "CMSinpAddress" */ "@sendmagic/prismic-nextjs/src/components/InputAddress"
  ),
);
const ProcessExplainWithIconsAndArrows = dynamic(() =>
  import(
    /* webpackChunkName: "CMSprocExpWIconsAArr" */ "@sendmagic/prismic-nextjs/src/components/ProcessExplainWithIconsAndArrows"
  ),
);
const TextWithDiagonalImages = dynamic(() =>
  import(
    /* webpackChunkName: "CMStxtWDiagImgs" */ "@sendmagic/prismic-nextjs/src/components/TextWithDiagonalImages"
  ),
);
const TextWithImageOverlap = dynamic(() =>
  import(
    /* webpackChunkName: "CMStxtWImgOverlap" */ "@sendmagic/prismic-nextjs/src/components/TextWithImageOverlap"
  ),
);
const Showcase = dynamic(() =>
  import(/* webpackChunkName: "CMSshowcase" */ "@sendmagic/prismic-nextjs/src/components/Showcase"),
);

// Modular Slices
const HeadingWithImagesDescription = dynamic(() =>
  import(
    /* webpackChunkName: "CMSheadWImgsDesc" */ "@sendmagic/prismic-nextjs/src/components/slices/HeadingWithImagesDescription"
  ),
);
const WhitepaperListing = dynamic(() =>
  import(
    /* webpackChunkName: "CMSwhitepaperListing" */ "@sendmagic/prismic-nextjs/src/components/slices/WhitepaperListing"
  ),
);
const AnimatedImageSlider = dynamic(() =>
  import(
    /* webpackChunkName: "CMSanimatedImgSlider" */ "@sendmagic/prismic-nextjs/src/components/slices/AnimatedImageSlider"
  ),
);
const BulletPointOnLeft = dynamic(() =>
  import(
    /* webpackChunkName: "CMSbulletPointOLeft" */ "@sendmagic/prismic-nextjs/src/components/slices/BulletPointOnLeft"
  ),
);
const PricingSection = dynamic(() =>
  import(
    /* webpackChunkName: "CMSpricingSection" */ "@sendmagic/prismic-nextjs/src/components/slices/PricingSection"
  ),
);
const AnimatedHeroBanner = dynamic(() =>
  import(
    /* webpackChunkName: "CMSanimatedHeroBanner" */ "@sendmagic/prismic-nextjs/src/components/slices/AnimatedHeroBanner"
  ),
);
const TwoImageRowWithTwoCTA = dynamic(() =>
  import(
    /* webpackChunkName: "CMS2ImgRowW2Cta" */ "@sendmagic/prismic-nextjs/src/components/slices/TwoImageRowWithTwoCTA"
  ),
);
const VideoSlice = dynamic(() =>
  import(
    /* webpackChunkName: "CMSvidSlice" */ "@sendmagic/prismic-nextjs/src/components/slices/VideoSlice"
  ),
);
const FeaturesHomepage = dynamic(() =>
  import(
    /* webpackChunkName: "CMSfeaturesHome" */ "@sendmagic/prismic-nextjs/src/components/slices/FeaturesHomePage"
  ),
);
const ToggleableTabs = dynamic(() =>
  import(
    /* webpackChunkName: "CMStgTabs" */ "@sendmagic/prismic-nextjs/src/components/slices/ToggleableTabs"
  ),
);
const VerticalCards = dynamic(() =>
  import(
    /* webpackChunkName: "CMSvertCards" */ "@sendmagic/prismic-nextjs/src/components/slices/VerticalCards"
  ),
);

const StartOrderBtn = ({
  className = "cursor-pointer btn btn-primary text-white text-uppercase fs-12 ls-1 font-weight-bold py-md-3 py-2 lh-1-2 w-248px w-md-auto",
  children,
  data = {},
  testId = "",
  ...rest
}) => {
  return (
    <div className={className} data-testid={testId}>
      {children}
    </div>
  );
};

export const DEFAULT_BACKGROUND_COLOR = "#283C30";
export const PRICING_TYPE = "pricing_type";

const sliceComponentMapping = {
  animated_image_slider: AnimatedImageSlider,
  header_slice: Banner,
  animated_hero_banner: AnimatedHeroBanner,
  home_banner: HomeBanner,
  text_with_icons_on_right: TextWithIconsOnRight,
  text_with_right_image: TextWithRightImage,
  text_with_small_image: TextWithSmallImage,
  text_with_left_image: TextWithLeftImage,
  text_with_horizontal_bullets: TextWithHorizontalBullets,
  features_: Features,
  large_cta_buttons_: ButtonsBlock,
  text_only_slice: ContentBlock,
  separator: Separator,
  testimonial: Testimonial,
  press: Press,
  pricing_section: PricingSection,
  case_study_slice: CaseStudy,
  how_it_works: HowItWorks,
  text_with_right_icons: TextWithRightIcons,
  comparison: SnackmagicSnackboxComparison,
  snack_giver_guarantee: SnackGiverGuarantee,
  product_slider: ProductSlider,
  timeline_slice: Timeline,
  faq: Faq,
  toggleable_tabs: ToggleableTabs,
  three_image_banner: ThreeImageBanner,
  customization_detail: CustomizationDetail,
  features_block: FeaturesBlock,
  four_image_row: FourImageRow,
  three_image_row: ThreeImageRow,
  two_image_row: TwoImageRow,
  two_image_row_with_two_cta: TwoImageRowWithTwoCTA,
  questions: Questions,
  text_with_ctas_and_overlapping_image: TextWithCTAandOverlappingImage,
  text_with_heading: TextWithHeading,
  text_only_banner: TextOnlyBanner,
  yellow_background_text: YellowBackgroundText,
  heading_with_points: HeadingWithPoints,
  calendly: Calendly,
  heading_with_button: HeadingWithButton,
  difference: Difference,
  long_block_with_text_and_images: LongBlockWithTextAndImages,
  large_image_header: LargeImageHeader,
  team: Team,
  did_you_know: DidYouKnow,
  text_with_background: TextWithBackground,
  colored_background_with_icons: ColoredBackgroundWithIcons,
  pricing_block: PricingBlock,
  pricing_package: PricingPackage,
  image_slider: ImageSlider,
  extended_cards_block: ExtendedCardsBlock,
  image_slider_with_multiple_rows: ImageSliderWithMultipleRows,
  features_small_icons: FeaturesSmallIcons,
  features_homepage: FeaturesHomepage,
  customer_testimonial: CustomerTestimonialSlider,
  video_slice: VideoSlice,
  multiple_video_slice: MultipleVideoSlice,
  two_cta_slice: TwoCtaSlice,
  image_gallery: ImageGallery,
  horizontal_scrollable_gallery: HorizontalScrollableGallery,
  inverted_how_it_works: InvertedHowItWorks,
  hero_slice_with_3_icons: HeroSlice,
  code_embed: EmbededResourceSlice,
  calendar_moments: CalendarMoments,
  listing_block_module: ListingBlock,
  html_embed: HtmlEmbed,
  vertical_feature_two_image: VerticalFeatureTwoImage,
  whitepaper_listing: WhitepaperListing,
  whitepaper_landing_page_slice: WhitepaperLandingPageSlice,
  country_stashes: CountryStashes,
  download_pdf: DownloadPdf,
  navigator_tab: NavigatorTab,
  features_list: FeaturesList,
  images_stack_container: ImagesStackContainer,
  background_image_with_text: BackgroundImageWithText,
  vertical_cards: VerticalCards,
  four_large_image_row: FourLargeImageRow,
  curved_block_text_with_image: CurvedBlockTextWithImage,
  two_columns_layout: TwoColumnsLayout,
  hover_overlays: HoverOverlays,
  card_section_with_responsive_background_image: CardSectionWithResponsiveBGImage,
  text_with_image_and_ctas: TextWithImagesAndCtas,
  text_slice_sleek: TextSliceSleek,
  table_listings: TableListings,
  text_with_thumbnail: TextWithThumbnail,
  input_address: InputAddress,
  process_explain_with_icons_and_arrows: ProcessExplainWithIconsAndArrows,
  text_with_diagonal_images: TextWithDiagonalImages,
  text_with_image_overlap: TextWithImageOverlap,
  showcase: Showcase,
  bullet_point_on_left: BulletPointOnLeft,
  heading_with_images_description: HeadingWithImagesDescription,
};

const secondaryProps = { backgroundColor: DEFAULT_BACKGROUND_COLOR };

const LazyLoadPlaceholder = ({ id }) => {
  if (id) {
    return <div className="lazyload-placeholder height-500" id={id} />;
  }
  return <div className="lazyload-placeholder height-500" />;
};

const renderPricingTypeDoc = (doc, commonProps) => {
  const groupBySlice =
    doc?.data?.body?.reduce((sliceGroup, slice, i, sliceArray) => {
      if (!i || sliceArray[i - 1].slice_type !== slice.slice_type) {
        sliceGroup.push([]);
      }
      sliceGroup[sliceGroup.length - 1].push(slice);
      return sliceGroup;
    }, []) || [];

  return groupBySlice.map((slice, idx) => {
    const sliceType = slice[0].slice_type;
    const Slice = sliceComponentMapping[sliceType];
    if (!Slice) return null;
    if (sliceType === "pricing_package") {
      return <Slice key={`pricing-slice-${idx}`} doc={slice} {...commonProps} />;
    } else {
      return <Slice key={`pricing-slice-${idx}`} doc={slice[0]} {...commonProps} />;
    }
  });
};

const PrismicMainComponent = ({ doc, lazyload = false }) => {
  const currentDevice = useContext(CurrentDeviceContext);

  const commonProps = {
    currentDevice,
    StartOrderBtn,
    ...secondaryProps,
  };

  if (doc?.type === PRICING_TYPE) {
    return <>{renderPricingTypeDoc(doc, commonProps)}</>;
  }

  const slices = doc?.data?.body || [];

  return (
    <>
      {slices.map((slice, index) => {
        const Slice = sliceComponentMapping[slice.slice_type];
        if (Slice) {
          if (lazyload && slice.slice_type === "video_slice") {
            return (
              <SSRLazyLoadResource
                key={`slice-${index}`}
                offset={100}
                height={200}
                placeholder={<LazyLoadPlaceholder id={slice?.primary?.internal_link_id} />}
              >
                <Slice doc={slice} {...commonProps} />
              </SSRLazyLoadResource>
            );
          }
          return <Slice key={`slice-${index}`} doc={slice} {...commonProps} />;
        }
        return null;
      })}
    </>
  );
};

export default PrismicMainComponent;
